import React, { useState, useEffect, useContext } from 'react';
import classNames from 'classnames/bind';
import styles from './AdminDashboardPage.module.css';
import { ToastContext } from '~/components/context/ToastContext';
import { ModalContext } from '~/components/context/ModalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTrash } from '@fortawesome/free-solid-svg-icons';
import { BASE_TRANSACTIONS_URL } from '~/helper/apiService';

const cx = classNames.bind(styles);

function CustomerHistory() {
  const [phone, setPhone] = useState('');
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState([]);
  const [customerPoints, setCustomerPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pointsLoading, setPointsLoading] = useState(true);
  const [pointsError, setPointsError] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);

  useEffect(() => {
    const fetchCustomerPoints = async (page = 1) => {
      try {
        setPointsLoading(true);
        const response = await fetch(`${BASE_TRANSACTIONS_URL}get-customer-points?page=${page}&limit=${limit}`, {
          credentials: 'include',
        });
        const data = await response.json();
        if (response.ok) {
          setCustomerPoints(data.customerPoints || []);
          setCurrentPage(data.pagination.page);
          setTotalPages(data.pagination.totalPages);
          setTotal(data.pagination.total);
        } else {
          showToast(data.error || 'Lỗi khi lấy dữ liệu tổng điểm khách hàng', 'error');
        }
      } catch (err) {
        showToast('Lỗi kết nối đến server', 'error');
      } finally {
        setPointsLoading(false);
      }
    };

    fetchCustomerPoints(currentPage);
  }, [currentPage]);

  const handleSearch = async () => {
    if (!phone) {
      showToast('Vui lòng nhập số điện thoại', 'error');
      return;
    }

    setLoading(true);
    setShowResults(true);
    try {
      const response = await fetch(`${BASE_TRANSACTIONS_URL}history?phone=${phone}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setHistory(data.history.map(row => ({
          timestamp: row[0],
          phone: row[1],
          points: parseInt(row[2]) || 0,
        })));
        setSummary(data.summary.map(row => ({
          timestamp: row[0],
          phone: row[1],
          totalPoints: parseInt(row[2]) || 0,
        })));
      } else {
        showToast(data.error || 'Có lỗi xảy ra', 'error');
        setHistory([]);
        setSummary([]);
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
      setHistory([]);
      setSummary([]);
    }
    setLoading(false);
  };

  const handleDelete = async (timestamp, phone) => {
    try {
      const response = await fetch(
        `${BASE_TRANSACTIONS_URL}delete-transaction?timestamp=${encodeURIComponent(timestamp)}&phone=${encodeURIComponent(phone)}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      const data = await response.json();
      if (response.ok) {
        showToast("Xóa giao dịch thành công", "success");
        await handleSearch(); // Refresh history after deletion
      } else {
        showToast(data.error || "Lỗi khi xóa giao dịch", "error");
        return false; // Keep modal open on error
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
      return false; // Keep modal open on error
    }
  };

  const confirmDelete = (timestamp, phone) => {
    showModal({
      title: "Xác Nhận Xóa",
      message: `Bạn có chắc chắn muốn xóa giao dịch lúc ${timestamp} cho số điện thoại ${phone}?`,
      onConfirm: () => handleDelete(timestamp, phone),
      onCancel: null, // Uses default hideModal from ModalContext
    });
  };

  const handleCustomerClick = (phoneNumber) => {
    setPhone(phoneNumber);
  };

  const handleBack = () => {
    setShowResults(false);
    setPhone('');
    setHistory([]);
    setSummary([]);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className={cx('customer-section')}>
      <div className={cx('search-form')}>
        <input
          type="text"
          placeholder="Nhập số điện thoại"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={cx('input')}
        />
        <button
          onClick={handleSearch}
          className={cx('button')}
        >
          Tìm Kiếm
        </button>
      </div>
      {showResults ? (
        <>
          <div style={{ textAlign: 'left', marginBottom: '20px' }}>
            <button
              onClick={handleBack}
              className={cx('back-button')}
              title="Quay Lại"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          </div>
          {loading ? (
            <p className={cx('loading')}>Đang tải dữ liệu...</p>
          ) : history.length === 0 ? (
            <p className={cx('no-data')}>Không có giao dịch nào</p>
          ) : (
            <>
              {summary.length > 0 && (
                <p className={cx('total-points')}>
                  Tổng Điểm: <span className={cx({ positive: summary[0].totalPoints > 0, negative: summary[0].totalPoints < 0 })}>
                    {summary[0].totalPoints}
                  </span>
                </p>
              )}
              <table className={cx('table')}>
                <thead>
                  <tr>
                    <th className={cx('th')}>Thời Gian</th>
                    <th className={cx('th')}>Số Điện Thoại</th>
                    <th className={cx('th')}>Điểm</th>
                    <th className={cx('th')}>Hành Động</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((transaction, index) => (
                    <tr key={index} className={cx('tr')}>
                      <td className={cx('td')}>{transaction.timestamp}</td>
                      <td className={cx('td')}>{transaction.phone}</td>
                      <td className={cx('td', { positive: transaction.points > 0, negative: transaction.points < 0 })}>
                        {transaction.points}
                      </td>
                      <td className={cx('td')}>
                        <div className={cx('button-container')}>
                          <button
                            className={cx('delete-button')}
                            onClick={() => confirmDelete(transaction.timestamp, transaction.phone)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      ) : (
        <div className={cx('customer-points-section')}>
          <h2 className={cx('subtitle')}>Danh Sách Tổng Điểm Khách Hàng</h2>
          {pointsLoading && <div className={cx('loading')}>Đang tải...</div>}
          {pointsError && (
            <div className={cx('message', 'error')}>
              {pointsError}
            </div>
          )}
          {!pointsLoading && !pointsError && customerPoints.length === 0 && (
            <div className={cx('no-data')}>Không có dữ liệu tổng điểm khách hàng</div>
          )}
          {!pointsLoading && !pointsError && customerPoints.length > 0 && (
            <>
              <div className={cx("pagination-info")}>
                Hiển thị {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, total)} trong tổng số {total} khách hàng
              </div>
              <table className={cx('table')}>
                <thead>
                  <tr className={cx('tr')}>
                    <th className={cx('th')}>Thời gian</th>
                    <th className={cx('th')}>Số điện thoại</th>
                    <th className={cx('th')}>Tổng điểm</th>
                  </tr>
                </thead>
                <tbody>
                  {customerPoints.map((point, index) => (
                    <tr key={index} className={cx('tr')}>
                      <td className={cx('td')}>{point.timestamp}</td>
                      <td
                        className={cx('td', 'clickable')}
                        onClick={() => handleCustomerClick(point.phone)}
                        style={{ cursor: 'pointer', color: '#4caf50', textDecoration: 'underline' }}
                      >
                        {point.phone}
                      </td>
                      <td
                        className={cx('td', {
                          positive: point.totalPoints > 0,
                          negative: point.totalPoints < 0,
                        })}
                      >
                        {point.totalPoints}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className={cx("pagination")}>
                  <button
                    className={cx("pagination-button")}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Trước
                  </button>
                  <span className={cx("pagination-info-text")}>
                    Trang {currentPage} / {totalPages}
                  </span>
                  <button
                    className={cx("pagination-button")}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Sau
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CustomerHistory;